import { ErrorMessage, Form, Formik } from 'formik'
import moment from 'moment'
import React, { useEffect } from 'react'
import { Button, Table } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import Select from 'react-select'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'
import * as Yup from 'yup'
import DeleteIcon from '../../../components/AllSvgIcon/DeleteIcon'
import EditIcon from '../../../components/AllSvgIcon/EditIcon'
import PlusIcon from '../../../components/AllSvgIcon/PlusIcon'
import HKBadge from '../../../components/Badges'
import FormikControl from '../../../components/FormikControl'
import Loader from '../../../components/Loader'
import ReactSelect from '../../../components/ReactSelect'
import SellerModal from '../../../components/SellerModal'
import TextError from '../../../components/TextError'
import { customStyles } from '../../../components/customStyles'
import {
  callApi,
  encodeId,
  focusInput,
  prepareNotificationData,
  showToast
} from '../../../lib/AllGlobalFunction'
import {
  allCrudNames,
  allPages,
  checkPageAccess
} from '../../../lib/AllPageNames'
import { _status_, isInventoryModel } from '../../../lib/AllStaticVariables'
import axiosProvider from '../../../lib/AxiosProvider'
import { _alphabetRegex_, _phoneNumberRegex_ } from '../../../lib/Regex'
import { _SwalDelete, _exception } from '../../../lib/exceptionMessage'

const WarehouseModal = ({
  loading,
  setLoading,
  initialValues,
  setInitialValues,
  modalShow,
  setModalShow,
  isModalRequired,
  fetchData,
  toast,
  setToast,
  initValues
}) => {
  const [allState, setAllState] = useImmer({
    country: [],
    city: [],
    state: [],
    gstDetails: []
  })
  const location = useLocation()
  const { user } = useSelector((state) => state)
  const { pageAccess } = useSelector((state) => state.user)

  const fetchDataFromApis = async (endpoint, queryString, setterFunc) => {
    const response = await callApi(endpoint, queryString)
    return setterFunc(response)
  }

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Please enter Name'),
    contactPersonName: Yup.string()
      .matches(_alphabetRegex_, 'Only alphabet allowed')
      .required('Please enter Contact Person Name'),
    contactPersonMobileNo: Yup.string()
      .required('Please enter Contact Person Number')
      .matches(_phoneNumberRegex_, 'Invalid mobile number')
      .length(10, 'Mobile Number should be exactly 10 digits long'),
    addressLine1: Yup.string().required('Please enter Address Line'),
    pincode: Yup.string()
      .required('Pincode is required')
      .matches(/^\d{6}$/, 'Pincode must be a 6-digit number'),
    countryId: Yup.string()
      .test('nonull', 'Please select Country', (value) => value !== 'undefined')
      .required('Please select Country'),
    stateId: Yup.string()
      .test('nonull', 'Please select State', (value) => value !== 'undefined')
      .required('Please select State'),
    cityId: Yup.string()
      .test('nonull', 'Please select City', (value) => value !== 'undefined')
      .required('Please select City'),
    gstInfoId:
      typeof gstDetails === 'object'
        ? Yup.string().notRequired()
        : Yup.string()
            .test(
              'nonull',
              'Please select Tax Number',
              (value) => value !== 'undefined'
            )
            .required('Please select Tax Number'),
    status: Yup.string()
      .test('nonull', 'Please select Status', (value) => value !== 'undefined')
      .required('Please select Status')
  })

  const handleDelete = async (id, userId) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'DELETE',
        endpoint: `seller/Warehouse?id=${id}&userId=${userId}`
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        setInitialValues({
          ...initialValues,
          warehouse: initialValues?.warehouse?.filter((x) => x.id !== id)
        })
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)
      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const csv = async (countryId = null, stateId = null) => {
    if (!allState?.country?.length) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'Country/Search',
        queryString: `?pageindex=0&PageSize=0`
      })
      if (response?.status === 200) {
        setAllState((draft) => {
          draft.country = response?.data?.data
        })
      }
    }

    if (countryId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'State/ByCountryId',
        queryString: `?id=${countryId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.state = response?.data?.data
        })
      }
    }

    if (stateId) {
      const response = await axiosProvider({
        method: 'GET',
        endpoint: 'City/ByStateId',
        queryString: `?id=${stateId}`
      })

      if (response?.status === 200) {
        setAllState((draft) => {
          draft.city = response?.data?.data
        })
      }
    }
  }

  const onSubmit = async (values) => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: values?.id ? 'PUT' : 'POST',
        endpoint: 'seller/Warehouse',
        data: values,
        location: location?.pathname,
        userId: user?.userInfo?.userId,
        oldData: initialValues?.warehouse
      })
      setLoading(false)
      if (response?.data?.code === 200) {
        if (!values?.id && !initialValues?.allWarehouse) {
          setModalShow((draft) => {
            draft.createSeller = false
            draft.basicInfo = false
            draft.gstInfo = false
            draft.warehouse = false
          })
        } else {
          const response = await axiosProvider({
            method: 'GET',
            endpoint: `seller/Warehouse/WarehouseSearch?UserID=${initialValues?.warehouse?.userID}`
          })

          if (response?.status === 200) {
            setInitialValues({
              ...initialValues,
              warehouse: response?.data?.data
            })
          } else {
            setModalShow((draft) => {
              draft.warehouse = false
            })
          }
        }
        axiosProvider({
          endpoint: 'Notification',
          method: 'POST',
          data: prepareNotificationData({
            reciverId: values?.userID,
            userId: user?.userInfo?.userId,
            userType: user?.userInfo?.userType,
            notificationTitle: `Warehouse: ${values?.name} ${
              values?.id
                ? 'updated warehouse successfully'
                : 'added warehouse successfully'
            }`,
            notificationDescription: `${values?.id ? 'Update' : 'Created'} by ${
              user?.userInfo?.fullName
            }`,
            url: `/manage-seller/seller-details/${encodeId(values?.userID)}`,
            notifcationsof: 'Seller'
          })
        })

        fetchData()
      }
      showToast(toast, setToast, response)
    } catch {
      setLoading(false)

      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  const renderComponent = () => {
    if (!Array.isArray(initialValues?.warehouse)) {
      return (
        <Formik
          enableReinitialize
          initialValues={initialValues?.warehouse}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {({ values, setFieldValue, validateForm }) => (
            <Form id='warehouse' className='add_seller_form'>
              <h3 className='my-3 head_h3'>Pickup Address</h3>
              <div className='row'>
                <div className='col-md-6'>
                  {Array.isArray(allState?.gstDetails) ? (
                    <div className='input-file-wrapper mb-3'>
                      <label className='form-label required'>Tax No</label>
                      <Select
                        id='gstInfoId'
                        menuPortalTarget={document.body}
                        value={
                          values?.gstInfoId && {
                            value: values?.gstInfoId,
                            label: values?.gstNo
                          }
                        }
                        styles={customStyles}
                        isDisabled={values?.id ? true : false}
                        options={
                          allState?.gstDetails?.length
                            ? allState?.gstDetails
                                ?.filter((x) => x.id)
                                ?.map(({ id, gstNo }) => ({
                                  value: id,
                                  label: gstNo
                                }))
                            : []
                        }
                        onChange={(e) => {
                          if (e) {
                            setFieldValue('gstInfoId', e?.value)
                          }
                        }}
                      />
                      <ErrorMessage name='gstInfoId' component={TextError} />
                    </div>
                  ) : (
                    <div className='input-file-wrapper mb-3'>
                      <FormikControl
                        isRequired
                        disabled
                        control='input'
                        label='Tax No'
                        id='gstNo'
                        type='text'
                        onBlur={(e) => {
                          let fieldName = e?.target?.name
                          setFieldValue(fieldName, values[fieldName]?.trim())
                        }}
                        name='gstNo'
                        value={values?.gstNo}
                        placeholder='Tax No'
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Name'
                      id='name'
                      type='text'
                      name='name'
                      value={values?.name}
                      placeholder='Name'
                      onChange={(e) => {
                        const inputText = e?.target?.value
                        const fieldName = e?.target?.name
                        const isValid = _alphabetRegex_.test(inputText)
                        if (isValid || !inputText) {
                          setFieldValue([fieldName], inputText)
                        }
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Contact Person Name'
                      id='contactPersonName'
                      type='text'
                      value={values?.contactPersonName}
                      name='contactPersonName'
                      placeholder='Contact Person Name'
                      onChange={(e) => {
                        const inputText = e?.target?.value
                        const fieldName = e?.target?.name
                        const isValid = _alphabetRegex_.test(inputText)
                        if (isValid || !inputText) {
                          setFieldValue([fieldName], inputText)
                        }
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Contact Person Mobile No'
                      id='contactPersonMobileNo'
                      maxLength='15'
                      type='text'
                      name='contactPersonMobileNo'
                      placeholder='Contact Person Mobile No'
                      onChange={(e) => {
                        let inputValue = e?.target?.value
                        let isValid = _phoneNumberRegex_.test(inputValue)
                        let fieldName = e?.target?.name
                        if (!inputValue || isValid) {
                          setFieldValue(fieldName, inputValue)
                        }
                      }}
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Address Line 1'
                      id='addressLine1'
                      type='text'
                      value={values?.addressLine1}
                      name='addressLine1'
                      placeholder='Address Line 1'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='Address Line 2'
                      id='addressLine2'
                      type='text'
                      value={values?.addressLine2}
                      name='addressLine2'
                      placeholder='Address Line 2'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      control='input'
                      label='Landmark'
                      id='landmark'
                      type='text'
                      value={values?.landmark}
                      name='landmark'
                      placeholder='Landmark'
                      onBlur={(e) => {
                        let fieldName = e?.target?.name
                        setFieldValue(fieldName, values[fieldName]?.trim())
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <FormikControl
                      isRequired
                      control='input'
                      label='Pincode'
                      id='pincode'
                      type='text'
                      maxLength={6}
                      name='pincode'
                      placeholder='Pincode'
                      onBlur={async (e) => {
                        setFieldValue('countryId', null)
                        setFieldValue('stateId', null)
                        setFieldValue('cityId', null)
                        if (e?.target?.value?.length === 6) {
                          const response = await axiosProvider({
                            method: 'GET',
                            endpoint: `Delivery/byPincode?pincode=${e?.target?.value}`
                          })
                          if (response?.status === 200) {
                            if (response?.data?.data?.countryID) {
                              let { data } = response?.data

                              if (data?.countryID) {
                                csv(data?.countryID)
                              }

                              if (data?.stateID) {
                                csv(null, data?.stateID)
                              }

                              setFieldValue('countryId', data?.countryID)
                              setFieldValue('countryName', data?.countryName)

                              setFieldValue('stateId', data?.stateID)
                              setFieldValue('stateName', data?.stateName)

                              setFieldValue('cityId', data?.cityID)
                              setFieldValue('cityName', data?.cityName)
                            } else {
                              if (!allState?.country?.length) {
                                csv()
                              }

                              setAllState((draft) => {
                                draft.state = []
                                draft.city = []
                              })
                            }
                          }
                        }
                      }}
                      onChange={(event) => {
                        const inputValue = event.target.value
                        const regex = /^[0-9\b]+$/
                        if (inputValue === '' || regex.test(inputValue)) {
                          setFieldValue('pincode', inputValue)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>Country</label>
                    <ReactSelect
                      id='countryId'
                      name='countryId'
                      value={
                        values?.countryId && {
                          value: values?.countryId,
                          label: values?.countryName
                        }
                      }
                      options={
                        allState?.country
                          ? allState?.country?.map(({ id, name }) => ({
                              value: id,
                              label: name
                            }))
                          : []
                      }
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('countryId', e?.value)
                          setFieldValue('countryName', e?.label)
                          setFieldValue('stateId', null)
                          setFieldValue('cityId', null)
                          setAllState((draft) => {
                            draft.state = []
                            draft.city = []
                          })
                          csv(e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>State</label>
                    <ReactSelect
                      id='stateId'
                      name='stateId'
                      value={
                        values?.stateId && {
                          value: values?.stateId,
                          label: values?.stateName
                        }
                      }
                      options={
                        allState?.state
                          ? allState?.state?.map(({ id, name }) => ({
                              label: name,
                              value: id
                            }))
                          : []
                      }
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('cityId', null)
                          setFieldValue('stateId', e?.value)
                          setFieldValue('stateName', e?.label)
                          setFieldValue((draft) => {
                            draft.city = []
                          })

                          csv(null, e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>City</label>
                    <ReactSelect
                      id='cityId'
                      name='cityId'
                      value={
                        values?.cityId && {
                          value: values?.cityId,
                          label: values?.cityName
                        }
                      }
                      options={
                        allState?.city
                          ? allState?.city?.map(({ id, name }) => ({
                              label: name,
                              value: id
                            }))
                          : []
                      }
                      onChange={(e) => {
                        setFieldValue('cityId', e?.value ?? null)
                        setFieldValue('cityName', e?.label ?? '')
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className='row'>
                <div className='col-md-6'>
                  <div className='input-file-wrapper mb-3'>
                    <label className='form-label required'>Status</label>
                    <ReactSelect
                      id='status'
                      name='status'
                      value={
                        values?.status && {
                          value: values?.status,
                          label: values?.status
                        }
                      }
                      options={_status_}
                      onChange={(e) => {
                        if (e) {
                          setFieldValue('status', e?.value)
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className='row justify-content-center'>
                <button
                  type='submit'
                  className='btn btn-th-blue btn btn-primary m-auto w-auto'
                  onClick={() => {
                    validateForm()?.then((focusError) =>
                      focusInput(Object?.keys(focusError)?.[0])
                    )
                  }}
                >
                  Submit
                </button>
              </div>
            </Form>
          )}
        </Formik>
      )
    } else {
      return (
        <>
          <div className='d-flex justify-content-end align-items-center mb-3 gap-3'>
            {checkPageAccess(
              pageAccess,
              allPages?.seller,
              allCrudNames?.write
            ) && (
              <Button
                variant='primary'
                className='d-flex align-items-center gap-2 py-1 px-2 fw-semibold btn btn-warning'
                onClick={async () => {
                  let warehouse = initValues?.warehouse
                  warehouse = {
                    ...warehouse,
                    userID: initialValues?.warehouse[0]?.userID
                  }
                  setLoading(true)
                  const response = await axiosProvider({
                    method: 'GET',
                    endpoint: 'seller/GSTInfo/byUserId',
                    queryString: `?userId=${initialValues?.warehouse[0]?.userID}`
                  })
                  setLoading(false)

                  if (response?.status === 200) {
                    let gstDetails = response?.data?.data
                    if (!Array.isArray(gstDetails)) {
                      warehouse = {
                        ...warehouse,
                        gstNo: gstDetails?.gstNo,
                        gstInfoId: gstDetails?.id
                      }
                    }
                    setAllState((draft) => {
                      draft.gstDetails = gstDetails
                    })
                  }
                  setInitialValues({
                    ...initialValues,
                    warehouse,
                    allWarehouse: initialValues?.warehouse
                  })
                }}
              >
                <PlusIcon />
                Add new Pickup
              </Button>
            )}
          </div>
          <Table className='align-middle table-list hr_table_seller'>
            <thead className='align-middle'>
              <tr>
                <th className='text-nowrap'>Pickup Name</th>
                <th className='text-nowrap'>Tax Info</th>
                <th className='text-nowrap'>Address</th>
                <th className='text-nowrap'>Created At</th>
                <th className='text-nowrap'>Modified At</th>
                <th className='text-nowrap'>Status</th>
                {checkPageAccess(pageAccess, allPages?.seller, [
                  allCrudNames?.update,
                  allCrudNames?.delete
                ]) && <th className='text-nowrap'>Action</th>}
              </tr>
            </thead>
            <tbody>
              {initialValues?.warehouse?.length > 0 &&
                initialValues?.warehouse?.map((data) => (
                  <tr key={data?.id}>
                    <td>{data?.name}</td>
                    <td>
                      <div className='d-flex align-items-center gap-2 pt-1 pb-1'>
                        Tax No: {data?.gstNo}
                      </div>
                      <div className='d-flex align-items-center gap-2 pt-1 pb-1'>
                        Tax State: {data?.stateName}
                      </div>
                    </td>
                    <td>
                      {data?.addressLine1}
                      {data?.addressLine2 && ','} {data?.addressLine2}
                      {data?.landmark && ','} {data?.landmark}
                      {data?.cityName && ','} {data?.cityName} - {data?.pincode}
                    </td>
                    <td>
                      {data?.createdAt
                        ? moment(data?.createdAt).format('DD/MM/YYYY')
                        : '-'}
                    </td>
                    <td>
                      {data?.modifiedAt
                        ? moment(data?.modifiedAt).format('DD/MM/YYYY')
                        : '-'}
                    </td>
                    <td>
                      <HKBadge
                        badgesbgname={
                          data.status === 'Active' ? 'success' : 'danger'
                        }
                        badgesTxtname={data.status}
                        badgeClassname={''}
                      />
                    </td>
                    {checkPageAccess(pageAccess, allPages?.seller, [
                      allCrudNames?.update,
                      allCrudNames?.delete
                    ]) && (
                      <td className='text-center'>
                        <div className='d-flex gap-2 justify-content-center'>
                          {checkPageAccess(
                            pageAccess,
                            allPages?.seller,
                            allCrudNames?.update
                          ) && (
                            <span
                              onClick={async () => {
                                let selectedWarehouse =
                                  initialValues?.warehouse?.find(
                                    (x) => x.id === data.id
                                  )
                                setInitialValues({
                                  ...initialValues,
                                  warehouse: selectedWarehouse,
                                  allWarehouse: initialValues?.warehouse
                                })
                                csv(
                                  selectedWarehouse?.countryId,
                                  selectedWarehouse?.stateId
                                )
                              }}
                            >
                              <EditIcon bg={'bg'} />
                            </span>
                          )}
                          {checkPageAccess(
                            pageAccess,
                            allPages?.seller,
                            allCrudNames?.delete
                          ) && (
                            <span
                              onClick={() => {
                                Swal.fire({
                                  title: _SwalDelete.title,
                                  text: _SwalDelete.text,
                                  icon: _SwalDelete.icon,
                                  showCancelButton:
                                    _SwalDelete.showCancelButton,
                                  confirmButtonColor:
                                    _SwalDelete.confirmButtonColor,
                                  cancelButtonColor:
                                    _SwalDelete.cancelButtonColor,
                                  confirmButtonText:
                                    _SwalDelete.confirmButtonText,
                                  cancelButtonText: _SwalDelete.cancelButtonText
                                }).then((result) => {
                                  if (result.isConfirmed) {
                                    handleDelete(
                                      data?.id,
                                      data?.userID,
                                      data?.gstInfoId
                                    )
                                  } else if (result.isDenied) {
                                  }
                                })
                              }}
                            >
                              <DeleteIcon bg={'bg'} />
                            </span>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
            </tbody>
          </Table>
        </>
      )
    }
  }

  const fetchWarehouseData = async () => {
    try {
      setLoading(true)
      const response = await axiosProvider({
        method: 'GET',
        endpoint: `seller/Warehouse/WarehouseSearch?UserID=${
          initialValues?.warehouse?.userID
            ? initialValues?.warehouse?.userID
            : initialValues?.warehouse[0]?.userID
        }`
      })
      setLoading(false)
      if (response?.status === 200) {
        let apiResponse = response?.data?.data
        let warehouse = { ...initialValues?.warehouse }
        if (Array.isArray(apiResponse)) {
          if (apiResponse?.length !== 0) {
            warehouse = apiResponse
          }
        } else if (typeof apiResponse === 'object' && apiResponse !== null) {
          warehouse = apiResponse
        }
        setInitialValues({
          ...initialValues,
          warehouse
        })
        setModalShow((draft) => {
          draft.warehouse = true
        })
      }
    } catch {
      showToast(toast, setToast, {
        data: {
          message: _exception?.message,
          code: 204
        }
      })
    }
  }

  useEffect(() => {
    if (isInventoryModel) {
      fetchWarehouseData()
    }
  }, [])

  useEffect(() => {
    if (!Array.isArray(initialValues?.warehouse))
      fetchDataFromApis(
        'seller/GSTInfo/byUserId',
        `?userId=${initialValues?.warehouse?.userID}`,
        (data) => {
          let warehouse = initialValues?.warehouse
            ? initialValues?.warehouse
            : {
                ...initValues?.warehouse,
                userID: initialValues?.warehouse?.userID
              }
          let gstDetails = data
          if (!Array.isArray(gstDetails)) {
            warehouse = {
              ...warehouse,
              gstNo: gstDetails?.gstNo,
              gstInfoId: gstDetails?.id
            }
          }
          setAllState((draft) => {
            draft.gstDetails = gstDetails ?? []
          })

          setInitialValues({ ...initialValues, warehouse })
        }
      )
  }, [])

  useEffect(() => {
    if (!Array.isArray(initialValues?.warehouse)) csv()
  }, [initialValues?.warehouse])

  return (
    <>
      {loading ? (
        <Loader />
      ) : isModalRequired ? (
        <SellerModal
          show={modalShow?.warehouse}
          modalsize={'xl'}
          modalheaderclass={''}
          onHide={() => setModalShow((draft) => (draft.warehouse = false))}
          btnclosetext={''}
          closebtnvariant={''}
          backdrop={'static'}
          submitbtnclass={
            !Array.isArray(initialValues?.warehouse) ? '' : 'd-none'
          }
          buttonclass={'justify-content-start'}
          formbuttonid={'warehouse'}
          submitname={'Save'}
          modalclass={'create_seller'}
          modalheader={
            !initialValues?.warehouse?.id && (
              <>
                <span
                  onClick={() => {
                    setModalShow((draft) => {
                      draft.warehouse = false
                      draft.gstInfo = true
                    })
                  }}
                  href='/dashboard'
                  className='text-decoration-none text-black fs-4 d-flex align-items-center gap-1 fw-semibold text-capitalize'
                >
                  <i className='m-icon m-icon--arrow_back co-das'></i>
                </span>

                <div className='ml-auto position-relative'>
                  <ul id='progressbar'>
                    <li className='completed' id='account'>
                      <i className='m-icon m-icon--create_account active'></i>
                      <strong>Create Account</strong>
                    </li>
                    <li className='completed' id='personal'>
                      <i className='m-icon m-icon--kyc_info active'></i>
                      <strong>KYC Info</strong>
                    </li>
                    <li className='completed' id='payment'>
                      <i className='m-icon m-icon--gst_info active'></i>
                      <strong>Tax Info</strong>
                    </li>
                    <li className='active' id='confirm'>
                      <i className='m-icon m-icon--warehouse active'></i>
                      <strong>Pickup Address</strong>
                    </li>
                  </ul>
                </div>
              </>
            )
          }
        >
          {renderComponent()}
        </SellerModal>
      ) : (
        <>{renderComponent()}</>
      )}
    </>
  )
}

export default WarehouseModal
